import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { get, post, put, api } from '../api';
import { ContactPicker } from '../components/ContactPicker';
import { Problems } from '../components/Field';
import { Row } from '../components/FormRow';
import { HistoryTable } from '../components/HistoryTable';
import { StatusPill, type Status } from './Money';
import { VisitMessages } from './VisitMessages';
import { currentLocale, dayLabel } from '../dates';
import { hoursOf, minutesFrom } from '../duration';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { assignmentRef, visitRef } from '../numbers';

interface Person {
  id: number;
  name: string;
  is_lead: boolean;
  seen_at: string | null;
  accepted_at: string | null;
}
interface Detail {
  id: number;
  number?: number;
  assignment_id: number;
  assignment_number?: number;
  date: string;
  start_time: string;
  end_time: string;
  minutes: number;
  open: boolean;
  reason: string;
  customer_name: string;
  client_name: string;
  address: { line1: string; line2: string; locality: string; region: string; postcode: string };
  access_notes: string;
  office_notes: string;
  lead_id: number;
  lead_name: string;
  team: Person[];
  holiday: string | null;
  changed: boolean;
  charge: string;
  pay: string;
  charge_locked: string;
  pay_locked: string;
  cancellation_reason: string;
  reinstate_block: string;
  xero_invoice_ref: string;
  /** The linked Xero invoice's status; '' when the number is only recorded, or there is none. */
  xero_invoice_status?: string;
  /** The linked invoice's page here, when there is one. */
  xero_invoice_id?: number;
  xero_bill_ref: string;
  xero_invoice_block: string;
  xero_bill_block: string;
  /** Where its money stands on each side, as the Money tab says it. */
  money_status?: { client: Status; contractor: Status };
  checklist: { title: string; done: boolean }[];
  notes: { id: number; body: string; created_at: string; from: string }[];
  can_complete: boolean;
  complete_hint: string;
}

const REASON: Record<string, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  missed: 'Missed',
  rescheduled: 'Rescheduled',
  assignment_ended: 'Assignment ended',
};

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(currentLocale(), {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

export function VisitDetail() {
  const { id } = useParams();
  const [tick, setTick] = useState(0);
  // Kept here, not in the form, because the form starts afresh after every save.
  const [message, setMessage] = useState('');
  const loaded = useLoad(() => get<Detail>(`/api/visits/${id}`), [id, tick]);
  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  if (!loaded.data) {
    return (
      <>
        <p role="alert" className="error">
          {loaded.error}
        </p>
        <Link to="/schedule">Back to the schedule</Link>
      </>
    );
  }
  // Keyed by what was saved, so the form starts from the server's copy after every change:
  // once that copy has arrived, not while the one from before the save is still showing.
  return (
    <>
      <Editor
        key={`${id}-${tick}${loaded.loading ? '-loading' : ''}`}
        v={loaded.data}
        message={message}
        setMessage={setMessage}
        reload={() => setTick((t) => t + 1)}
      />
      <VisitMessages visitId={loaded.data.id} />
      <HistoryTable table="visits" id={loaded.data.id} />
    </>
  );
}

function Editor(props: {
  v: Detail;
  message: string;
  setMessage(m: string): void;
  reload(): void;
}) {
  const { v } = props;
  const [date, setDate] = useState(v.date);
  const [time, setTime] = useState(v.start_time);
  const [hours, setHours] = useState(hoursOf(v.minutes));
  const [charge, setCharge] = useState(v.charge);
  const [pay, setPay] = useState(v.pay);
  const [notes, setNotes] = useState(v.office_notes);
  const [xeroInvoice, setXeroInvoice] = useState(v.xero_invoice_ref);
  const [xeroBill, setXeroBill] = useState(v.xero_bill_ref);
  const [cover, setCover] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const { message, setMessage } = props;
  const [cancelling, setCancelling] = useState(false);
  const [chargeStill, setChargeStill] = useState(true);
  const [payStill, setPayStill] = useState(true);
  const [why, setWhy] = useState('');

  async function run(fn: () => Promise<unknown>, done: string, after = props.reload) {
    setProblems([]);
    setMessage('');
    try {
      await fn();
      setMessage(done);
      after();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    void run(
      () =>
        v.open
          ? put(`/api/visits/${v.id}`, {
              date,
              time,
              minutes: minutesFrom(hours),
              charge,
              pay,
              notes,
            })
          : put(`/api/visits/${v.id}/money`, { charge, pay }),
      'Saved.',
    );
  }

  const a = v.address;
  const where = [a.line1, a.line2, a.locality, a.region, a.postcode].filter(Boolean).join(', ');
  const invoiceDirty = xeroInvoice.trim() !== v.xero_invoice_ref;
  const billDirty = xeroBill.trim() !== v.xero_bill_ref;
  const refsDirty = invoiceDirty || billDirty;
  const dirty =
    date !== v.date ||
    time !== v.start_time ||
    minutesFrom(hours) !== v.minutes ||
    charge !== v.charge ||
    pay !== v.pay ||
    notes !== v.office_notes ||
    refsDirty;
  // Only a side that can take a number, or already has one, is worth showing.
  const canRefInvoice = v.xero_invoice_block === '' || v.xero_invoice_ref !== '';
  const canRefBill = v.xero_bill_block === '' || v.xero_bill_ref !== '';

  return (
    <form onSubmit={save} className="stack narrow" noValidate>
      <UnsavedChanges when={dirty} />
      <p>
        <Link to="/schedule">← Schedule</Link>
      </p>
      <div className="row start">
        <h1>{v.customer_name || 'Visit'}</h1>
        {v.number ? <span className="record-number">{visitRef(v.number)}</span> : null}
        <span className="muted">
          {dayLabel(v.date)}, {v.start_time}–{v.end_time}
        </span>
        <span className={v.open ? 'pill' : v.reason === 'completed' ? 'pill ok' : 'pill bad'}>
          {REASON[v.reason] ?? v.reason}
        </span>
      </div>
      {v.holiday && (
        <p className="notice" role="status">
          Public holiday: {v.holiday}. Confirm before heading out.
        </p>
      )}
      {v.changed && v.open && (
        <p className="notice" role="status">
          This visit was changed after it was created. Check that each contractor has seen it.
        </p>
      )}
      <Problems problems={problems} title="Not done." />
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}

      <section className="panel" aria-label="Details">
        <Row label="Date & time" wide>
          {v.open ? (
            <div className="inline">
              <input
                type="date"
                aria-label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <input
                type="time"
                aria-label="Time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
              <label className="inline-label">
                for
                <input
                  inputMode="decimal"
                  aria-label="Length in hours"
                  className="short"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
                hrs
              </label>
            </div>
          ) : (
            <span>
              {dayLabel(v.date)}, {v.start_time}–{v.end_time}
            </span>
          )}
        </Row>
        <Row label="Customer">
          <span>
            <Link to={`/assignments/${v.assignment_id}`}>{v.customer_name}</Link>
            {v.assignment_number ? (
              <span className="muted"> ({assignmentRef(v.assignment_number)})</span>
            ) : null}
            {a.locality && <span className="muted"> — {a.locality}</span>}
          </span>
          {v.client_name && <span className="muted">Invoiced to {v.client_name}</span>}
        </Row>
        {where && (
          <Row label="Address">
            <span>{where}</span>
            {v.access_notes && <span className="muted">Access: {v.access_notes}</span>}
          </Row>
        )}
        <Row
          label="Team"
          wide
          hint="The lead is who this visit is paid to. Change it here for this visit only; the assignment keeps its own lead."
        >
          <ul className="plain">
            {v.team.length === 0 && <li className="muted">Nobody is on this visit yet.</li>}
            {v.team.map((p) => (
              <li key={p.id} className="team-row">
                <span>
                  {p.name}
                  {p.is_lead ? ' (lead)' : ' (assist)'}
                </span>
                {v.open && !p.is_lead && (
                  <button
                    type="button"
                    className="quiet"
                    onClick={() =>
                      void run(
                        () => api('PUT', `/api/visits/${v.id}/lead`, { contractor_id: p.id }),
                        `${p.name} is now the lead for this visit.`,
                      )
                    }
                  >
                    Make lead<span className="sr-only"> {p.name}</span>
                  </button>
                )}
                {v.open && (
                  <button
                    type="button"
                    className="quiet"
                    onClick={() =>
                      void run(
                        () => api('DELETE', `/api/visits/${v.id}/contractors/${p.id}`),
                        `${p.name} removed from this visit.`,
                      )
                    }
                  >
                    Remove<span className="sr-only"> {p.name}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
          {v.open && (
            <div className="inline">
              <ContactPicker
                role="contractor"
                label="Add a contractor to this visit"
                value={cover}
                exclude={v.team.map((p) => p.id)}
                onChange={setCover}
              />
              <button
                type="button"
                className="quiet"
                disabled={cover === 0}
                onClick={() =>
                  void run(
                    () => api('PUT', `/api/visits/${v.id}/lead`, { contractor_id: cover }),
                    'Contractor is now the lead for this visit.',
                  )
                }
              >
                Add as lead
              </button>
              <button
                type="button"
                disabled={cover === 0}
                onClick={() =>
                  void run(
                    () => post(`/api/visits/${v.id}/contractors`, { contractor_id: cover }),
                    'Contractor added to this visit.',
                  )
                }
              >
                Add to visit
              </button>
            </div>
          )}
        </Row>
        <Row label="Checklist" wide>
          {v.checklist.length === 0 && <span className="muted">No tasks for this visit.</span>}
          <ul className="plain checks">
            {v.checklist.map((t) => (
              <li key={t.title}>
                <input type="checkbox" checked={t.done} disabled aria-label={t.title} />
                <span className={t.done ? 'done' : ''}>{t.title}</span>
              </li>
            ))}
          </ul>
        </Row>
        {!v.open && v.money_status && (
          <Row label="Invoice and bill" wide>
            <div className="inline">
              <span>
                Client: <StatusPill status={v.money_status.client} />
              </span>
              <span>
                Contractor: <StatusPill status={v.money_status.contractor} />
              </span>
            </div>
          </Row>
        )}
        {/* Why a side can't change is a note, not an error: nothing went wrong. */}
        <Row label="Money" wide>
          <div className="inline">
            <label className="inline-label">
              Client is charged $
              <input
                inputMode="decimal"
                className="short"
                aria-label="Charge"
                disabled={v.charge_locked !== ''}
                aria-describedby={v.charge_locked ? 'charge-locked' : undefined}
                value={charge}
                onChange={(e) => setCharge(e.target.value)}
              />
            </label>
            <label className="inline-label">
              Contractor is paid $
              <input
                inputMode="decimal"
                className="short"
                aria-label="Pay"
                disabled={v.pay_locked !== ''}
                aria-describedby={v.pay_locked ? 'pay-locked' : undefined}
                value={pay}
                onChange={(e) => setPay(e.target.value)}
              />
            </label>
          </div>
          {v.charge_locked && (
            <span className="muted hint" id="charge-locked">
              {v.charge_locked}
            </span>
          )}
          {v.pay_locked && (
            <span className="muted hint" id="pay-locked">
              {v.pay_locked}
            </span>
          )}
        </Row>
        {!v.open && (canRefInvoice || canRefBill) && (
          <>
            {canRefInvoice && (
              <Row
                label="Client invoice in Xero"
                wide
                hint="Invoiced the client in Xero directly? Enter that invoice's number: the visit is linked to it and follows its status. Clear it to put the visit back to be invoiced."
              >
                <div className="inline">
                  <input
                    className="short"
                    aria-label="Xero invoice number"
                    placeholder="INV-0000"
                    maxLength={50}
                    value={xeroInvoice}
                    onChange={(e) => setXeroInvoice(e.target.value)}
                  />
                  <button
                    type="button"
                    className="quiet"
                    disabled={!invoiceDirty}
                    onClick={() =>
                      void run(
                        () => put(`/api/visits/${v.id}/xero-invoice`, { number: xeroInvoice }),
                        xeroInvoice.trim()
                          ? 'Linked to the Xero invoice.'
                          : 'Xero invoice taken off.',
                      )
                    }
                  >
                    {v.xero_invoice_ref && !xeroInvoice.trim() ? 'Unlink' : 'Link invoice'}
                  </button>
                  {v.xero_invoice_status && !invoiceDirty && (
                    <Link to={`/invoices/${v.xero_invoice_id}`} className="pill info">
                      {v.xero_invoice_status}
                    </Link>
                  )}
                </div>
              </Row>
            )}
            {canRefBill && (
              <Row
                label="Contractor bill in Xero"
                wide
                hint="Paid the contractor through a bill in Xero directly? Enter the bill's number and the pay leaves the queue. Clear it to put it back."
              >
                <div className="inline">
                  <input
                    className="short"
                    aria-label="Xero bill number"
                    maxLength={50}
                    value={xeroBill}
                    onChange={(e) => setXeroBill(e.target.value)}
                  />
                  <button
                    type="button"
                    className="quiet"
                    disabled={!billDirty}
                    onClick={() =>
                      void run(
                        () => put(`/api/visits/${v.id}/xero-refs`, { bill: xeroBill }),
                        'Xero bill number saved.',
                      )
                    }
                  >
                    Save bill number
                  </button>
                </div>
              </Row>
            )}
          </>
        )}
        <Row label="Office notes" wide>
          <textarea
            aria-label="Office notes"
            value={notes}
            disabled={!v.open}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Row>
        <Row label="Contractor notes" wide>
          {v.notes.length === 0 && <span className="muted">No notes from the contractors.</span>}
          {v.notes.map((n) => (
            <div key={n.id} className="thread">
              <strong>{n.from || 'Contractor'}</strong>{' '}
              <span className="muted">{when(n.created_at)}</span>
              <div>{n.body}</div>
            </div>
          ))}
        </Row>
        <Row
          label="Seen status"
          wide
          hint="Confirm for someone when they have confirmed by phone or message. The log notes it was the office."
        >
          {v.team.length === 0 && <span className="muted">Nobody to notify.</span>}
          {v.team.map((p) => (
            <div key={p.id} className="team-row">
              <span>
                {p.name} <span className="muted">({p.is_lead ? 'lead' : 'assist'})</span> —{' '}
                {p.seen_at ? `seen ${when(p.seen_at)}` : 'not seen yet'}
                {p.accepted_at ? `, confirmed ${when(p.accepted_at)}` : ', not confirmed'}
              </span>
              {v.open && !p.accepted_at && (
                <button
                  type="button"
                  className="quiet"
                  onClick={() =>
                    void run(
                      () => post(`/api/visits/${v.id}/confirm`, { contact_id: p.id }),
                      `Confirmed for ${p.name}.`,
                    )
                  }
                >
                  Confirm for {p.name.trim().split(/\s+/)[0]}
                  <span className="sr-only"> ({p.name})</span>
                </button>
              )}
            </div>
          ))}
        </Row>
      </section>

      <div className="row start">
        {(v.open || v.charge_locked === '' || v.pay_locked === '') && (
          <button type="submit">Save changes</button>
        )}
        {v.open && (
          <button
            type="button"
            className="ok-fill"
            disabled={!v.can_complete}
            onClick={() => void run(() => post(`/api/visits/${v.id}/complete`), 'Visit completed.')}
          >
            Mark complete
          </button>
        )}
        {v.open && !cancelling && (
          <button type="button" className="danger" onClick={() => setCancelling(true)}>
            Cancel this visit
          </button>
        )}
        {(v.reason === 'cancelled' || v.reason === 'missed') && (
          <button
            type="button"
            disabled={v.reinstate_block !== ''}
            onClick={() => void run(() => post(`/api/visits/${v.id}/reinstate`), 'Visit reopened.')}
          >
            Reopen this visit
          </button>
        )}
      </div>
      {v.reinstate_block && (v.reason === 'cancelled' || v.reason === 'missed') && (
        <p className="muted">{v.reinstate_block}</p>
      )}
      {v.open && !v.can_complete && v.complete_hint && <p className="muted">{v.complete_hint}</p>}
      {v.cancellation_reason && <p className="muted">Reason given: {v.cancellation_reason}</p>}

      {cancelling && (
        <section className="panel" aria-label="Cancel this visit">
          <Row label="Cancel" wide hint="Choose what still happens with the money.">
            <label className="check">
              <input
                type="checkbox"
                checked={chargeStill}
                onChange={(e) => setChargeStill(e.target.checked)}
              />
              Still charge the client
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={payStill}
                onChange={(e) => setPayStill(e.target.checked)}
              />
              Still pay the contractor
            </label>
            <label>
              Reason (optional)
              <input value={why} maxLength={200} onChange={(e) => setWhy(e.target.value)} />
            </label>
            <div className="inline">
              <button
                type="button"
                className="danger"
                onClick={() =>
                  void run(
                    () =>
                      post(`/api/visits/${v.id}/cancel`, {
                        reason: 'cancelled',
                        chargeable: chargeStill,
                        payable: payStill,
                        why,
                      }),
                    'Visit cancelled.',
                  )
                }
              >
                Cancel the visit
              </button>
              <button type="button" className="quiet" onClick={() => setCancelling(false)}>
                Keep it
              </button>
            </div>
          </Row>
        </section>
      )}
    </form>
  );
}
